<template>
    <component
        :is="editable ? 'button' : 'span'"
        :type="editable ? 'button' : undefined"
        :class="['markup-chip', { 'is-override': isOverride, 'is-inherited': isInherited, 'is-editable': editable }]"
        :title="tooltip"
        @click="editable && emit('edit')"
    >
        <template v-if="isInherited">Наценка партнёра</template>
        <template v-else>
            <span v-for="part in parts" :key="part.level" class="markup-chip__part">
                <span class="markup-chip__level">{{ part.short }}</span> {{ part.value }}
            </span>
        </template>
    </component>
</template>

<script setup>
import { computed } from 'vue';
import { MARKUP_LEVEL_LABEL } from '../../../constants/structure';
import { effectiveMarkupPercent, formatPercent, hasMarkupOverride, visibleMarkupLevels } from '../../../utils/markupUtils';

const props = defineProps({
    /** Наценки цели: дефолт партнёра или переопределения кампании. */
    markup: { type: Object, required: true },
    /** Для кампании — дефолт её партнёра: пустой уровень кампании наследует его. */
    partnerMarkup: { type: Object, default: null },
    editable: { type: Boolean, default: false },
});

const emit = defineEmits(['edit']);

const isCampaign = computed(() => props.partnerMarkup !== null);
const isOverride = computed(() => isCampaign.value && hasMarkupOverride(props.markup));
const isInherited = computed(() => isCampaign.value && !isOverride.value);

const parts = computed(() => visibleMarkupLevels(props.markup)
    .filter(level => !isCampaign.value || props.markup[level] != null)
    .map(level => ({
        level,
        short: MARKUP_LEVEL_LABEL[level].short,
        value: formatPercent(props.markup[level] ?? 0),
    })));

const tooltip = computed(() => {
    const lines = visibleMarkupLevels(props.markup).map((level) => {
        const value = isCampaign.value
            ? effectiveMarkupPercent(props.markup, props.partnerMarkup, level)
            : props.markup[level];
        const suffix = isCampaign.value && props.markup[level] == null ? ' — как у партнёра' : '';
        return `${MARKUP_LEVEL_LABEL[level].full}: ${formatPercent(value ?? 0)}${suffix}`;
    });
    if (props.editable) lines.push('Нажмите, чтобы изменить');
    return lines.join('\n');
});
</script>

<style scoped lang="scss">
@use '../../../../scss/pages/admin/admin-tokens' as *;

.markup-chip {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: $spacing-sm;
    padding: 0.0625rem $spacing-sm;
    border: 1px solid $admin-border-color;
    border-radius: 999px;
    background: $admin-bg-secondary;
    font-size: $font-size-xs;
    font-weight: 500;
    line-height: 1.6;
    color: $admin-text-secondary;
    white-space: nowrap;

    &.is-editable {
        cursor: pointer;

        &:hover { border-color: $admin-primary-color; color: $admin-primary-color; }
    }

    &.is-override {
        border-color: #fcd34d;
        background: $admin-warning-bg;
        color: $admin-warning-color;
    }

    &.is-inherited {
        border-style: dashed;
        background: transparent;
        color: $admin-text-light;
    }
}

.markup-chip__level {
    font-weight: 700;
}
</style>
